import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Link } from "wouter";
import {
  Sun, CheckSquare, Mail, Inbox, Folder, MessageSquare,
  CheckCircle2, Circle, Sparkles, Send, ChevronRight,
  X, Layers, Plus, Clock, Flag,
  Bot, Star, Zap, LayoutGrid, List, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// ─── Palette nav items ────────────────────────────────────────────────────────
const NAV = [
  { id: "today",    Icon: Sun,           label: "Aujourd'hui", color: "text-amber-500" },
  { id: "actions",  Icon: CheckSquare,   label: "À faire",     color: "text-blue-500",  badge: 6 },
  { id: "mail",     Icon: Mail,          label: "Mail",        color: "text-slate-500", badge: 4 },
  { id: "inbox",    Icon: Inbox,         label: "Inbox",       color: "text-green-500" },
  { id: "projects", Icon: Folder,        label: "Projets",     color: "text-violet-500" },
  { id: "chat",     Icon: MessageSquare, label: "Chat IA",     color: "text-rose-500" },
];

const CYCLE_MS = 4000;
const PAGES = ["today", "actions", "projects", "chat"] as const;
type PageId = (typeof PAGES)[number];

// ─── Sidebar ─────────────────────────────────────────────────────────────────
const MOBILE_NAV = [
  { id: "today",    Icon: Sun,           label: "Aujourd'hui", color: "text-amber-500" },
  { id: "actions",  Icon: CheckSquare,   label: "À faire",     color: "text-blue-500" },
  { id: "projects", Icon: Folder,        label: "Projets",     color: "text-violet-500" },
  { id: "chat",     Icon: MessageSquare, label: "Chat IA",     color: "text-rose-500" },
] as const;

function MobileBottomNav({ active, onSelect }: { active: PageId; onSelect: (p: PageId) => void }) {
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex border-t border-border bg-background/95 backdrop-blur-sm">
      {MOBILE_NAV.map(({ id, Icon, label, color }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            onClick={() => onSelect(id as PageId)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 transition-colors ${
              isActive ? "text-primary" : "text-muted-foreground"
            }`}
          >
            <Icon className={`w-5 h-5 ${isActive ? "text-primary" : color}`} />
            <span className="text-[10px] font-medium leading-none">{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function Sidebar({ active, onSelect }: { active: PageId; onSelect: (p: PageId) => void }) {
  return (
    <aside className="hidden md:flex flex-col w-56 border-r border-border bg-sidebar shrink-0 h-full overflow-hidden">
      {/* Logo */}
      <div className="flex items-center gap-2 px-5 py-5 border-b border-border/60">
        <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <span className="font-bold text-base tracking-tight">OneNext</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-3 space-y-0.5">
        {NAV.map(({ id, Icon, label, color, badge }) => {
          const isActive = active === id;
          return (
            <button
              key={id}
              onClick={() => (PAGES as readonly string[]).includes(id) && onSelect(id as PageId)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              }`}
            >
              <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-primary" : color}`} />
              <span className="flex-1 text-left">{label}</span>
              {badge && (
                <span className="text-[10px] font-bold bg-primary/15 text-primary px-1.5 py-0.5 rounded-full">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 pb-4 pt-2 border-t border-border/60 space-y-1">
        <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/60 transition-colors">
          <Layers className="w-4 h-4 text-amber-500 shrink-0" />
          <span>Visite guidée</span>
        </button>
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg">
          <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-[11px] font-bold text-primary shrink-0">
            DÉ
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold truncate">Démo</p>
            <p className="text-[10px] text-muted-foreground truncate">demo@onenext.local</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

// ─── Demo banner ──────────────────────────────────────────────────────────────
function DemoBanner() {
  return (
    <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-sm text-amber-800 shrink-0">
      <Star className="w-3.5 h-3.5 text-amber-500 shrink-0" />
      <span className="font-semibold">Mode démo</span>
      <span className="hidden sm:inline text-amber-700/80">— vous naviguez avec des données d'exemple. Vos données réelles ne sont pas affectées.</span>
      <div className="ml-auto flex items-center gap-2">
        <Link href="/sign-up">
          <Button size="sm" className="h-7 px-3 text-xs gap-1">
            Créer un compte <ChevronRight className="w-3 h-3" />
          </Button>
        </Link>
        <Link href="/">
          <button className="text-amber-700 hover:text-amber-900 p-1 rounded transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </Link>
      </div>
    </div>
  );
}

// ─── TODAY page ───────────────────────────────────────────────────────────────
const todayTasks = [
  { label: "Répondre à Marc — devis Q3", time: "10 min", done: true,  priority: "IA: priorité max", ai: true },
  { label: "Préparer la revue produit 6 mai",  time: "45 min", done: false, priority: "Urgent" },
  { label: "Relire la proposition Acme Corp",   time: "20 min", done: false },
  { label: "Planifier sprint équipe — S19",    time: "30 min", done: false },
  { label: "Envoyer bilan hebdo à Camille",    time: "15 min", done: false },
];

function TodayView() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [checked, setChecked] = useState(new Set([0]));

  return (
    <div ref={ref} className="flex-1 overflow-y-auto p-6 max-w-2xl w-full mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.35 }}
        className="mb-6"
      >
        <div className="flex items-center gap-2 mb-1">
          <Sun className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold">Aujourd'hui</h1>
          <span className="text-sm text-muted-foreground">· Lun. 04 mai</span>
        </div>
        <p className="text-sm text-muted-foreground">
          L'IA a sélectionné vos <strong>5 priorités</strong> du jour parmi 23 tâches.
        </p>
      </motion.div>

      {/* AI summary card */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.35, delay: 0.08 }}
        className="mb-4 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 flex items-start gap-3"
      >
        <Bot className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold text-primary">Focus IA : </span>
          <span className="text-foreground/80">Répondre à Marc est critique — le devis expire vendredi. Ensuite, confirmez la revue produit.</span>
        </div>
      </motion.div>

      {/* Tasks */}
      <div className="space-y-2">
        {todayTasks.map((task, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -16 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.32, delay: 0.14 + i * 0.07 }}
            onClick={() =>
              setChecked((s) => {
                const n = new Set(s);
                n.has(i) ? n.delete(i) : n.add(i);
                return n;
              })
            }
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all select-none ${
              checked.has(i)
                ? "bg-primary/6 border-primary/20"
                : "bg-card border-border/60 hover:border-primary/30 hover:bg-muted/30"
            }`}
          >
            {checked.has(i) ? (
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-muted-foreground/40 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${checked.has(i) ? "line-through text-muted-foreground" : ""}`}>
                {task.label}
              </p>
            </div>
            {task.ai && !checked.has(i) && (
              <motion.span
                initial={{ scale: 0.6, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : {}}
                transition={{ delay: 0.65, type: "spring", stiffness: 380 }}
                className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0"
              >
                {task.priority}
              </motion.span>
            )}
            <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1">
              <Clock className="w-3 h-3" />{task.time}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Progress */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: 0.6 }}
        className="mt-6 pt-4 border-t border-border"
      >
        <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
          <span>Progression du jour</span>
          <span className="font-semibold text-primary">{checked.size} / {todayTasks.length}</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: 0 }}
            animate={inView ? { width: `${(checked.size / todayTasks.length) * 100}%` } : {}}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.65 }}
          />
        </div>
      </motion.div>
    </div>
  );
}

// ─── ACTIONS page ─────────────────────────────────────────────────────────────
const actionCards = [
  {
    initial: "M", sender: "Marc Dupont",   time: "08:42",
    subject: "Devis Q3 — validation requise avant vendredi",
    summary: "Marc attend ta validation pour avancer. Délai critique.",
    badge: "À répondre", badgeColor: "bg-blue-50 text-blue-700 border border-blue-200",
  },
  {
    initial: "S", sender: "Sophie Martin", time: "09:15",
    subject: "Revue produit 6 mai — ordre du jour",
    summary: "Confirme ta présence et l'ordre du jour pour cette session.",
    badge: "Planifier",   badgeColor: "bg-amber-50 text-amber-700 border border-amber-200",
  },
  {
    initial: "A", sender: "Acme Corp",     time: "07:30",
    subject: "Proposition de partenariat stratégique — délai 48h",
    summary: "Évalue et réponds avant mercredi soir.",
    badge: "Déléguer",   badgeColor: "bg-violet-50 text-violet-700 border border-violet-200",
  },
  {
    initial: "L", sender: "Lucas Bernard", time: "Yesterday",
    subject: "Sprint S19 — points de blocage à traiter",
    summary: "3 tickets bloqués, l'équipe attend un arbitrage.",
    badge: "Décider",    badgeColor: "bg-rose-50 text-rose-700 border border-rose-200",
  },
];

function ActionsView() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <div ref={ref} className="flex-1 overflow-y-auto p-6 max-w-2xl w-full mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        className="flex items-center gap-2 mb-6"
      >
        <CheckSquare className="w-5 h-5 text-blue-500" />
        <h1 className="text-xl font-bold">À faire</h1>
        <span className="text-xs bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full ml-1">6 actions IA</span>
      </motion.div>

      <div className="space-y-3">
        {actionCards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.38, delay: 0.1 + i * 0.09, ease: "easeOut" }}
            className="flex items-start gap-3.5 p-4 rounded-xl border border-border/70 bg-card hover:shadow-sm hover:border-primary/25 transition-all cursor-pointer"
          >
            <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
              {card.initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                <span className="text-sm font-semibold">{card.sender}</span>
                <span className="text-[11px] text-muted-foreground shrink-0">{card.time}</span>
              </div>
              <p className="text-[13px] font-medium text-foreground truncate">{card.subject}</p>
              <p className="text-[12px] text-muted-foreground mt-0.5 line-clamp-1">{card.summary}</p>
            </div>
            <motion.span
              initial={{ opacity: 0, scale: 0.7 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.3 + i * 0.09, type: "spring", stiffness: 350 }}
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${card.badgeColor}`}
            >
              {card.badge}
            </motion.span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── PROJECTS page ────────────────────────────────────────────────────────────
const demoProjects = [
  {
    name: "Refonte site web Artemia Group",
    done: 2, total: 6, priority: "Haute",
    deadline: "22 mai",
    nextAction: "Rédiger les textes de la page Accueil",
    priorityClass: "text-destructive",
  },
  {
    name: "Campagne lancement été 2026",
    done: 1, total: 5, priority: "Haute",
    deadline: "15 mai",
    nextAction: "Valider les BAT affiches campagne été",
    priorityClass: "text-destructive",
  },
  {
    name: "Podcast « Les Créatifs »",
    done: 1, total: 4, priority: "Basse",
    deadline: null,
    nextAction: "Enregistrer l'épisode 3",
    priorityClass: "text-muted-foreground",
  },
  {
    name: "Prospection nouveaux clients Q2",
    done: 0, total: 4, priority: "Moyenne",
    deadline: null,
    nextAction: null,
    priorityClass: "text-amber-600",
  },
];

function ProjectsView() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [view, setView] = useState<"grid" | "list">("grid");

  return (
    <div ref={ref} className="flex-1 overflow-y-auto p-6 max-w-3xl w-full mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <div className="flex items-center gap-2">
            <Folder className="w-5 h-5 text-violet-500" />
            <h1 className="text-xl font-bold">Projets</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">Vos grands objectifs en cours.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setView("grid")}
              className={`p-1.5 transition-colors ${view === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setView("list")}
              className={`p-1.5 transition-colors ${view === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}
            >
              <List className="w-3.5 h-3.5" />
            </button>
          </div>
          <button className="flex items-center gap-1.5 text-sm text-primary font-medium border border-primary/30 rounded-lg px-2.5 py-1.5 hover:bg-primary/5 transition-colors">
            <Plus className="w-4 h-4" /> Nouveau
          </button>
        </div>
      </motion.div>

      {view === "grid" ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {demoProjects.map((p, i) => {
            const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.35, delay: 0.1 + i * 0.08 }}
                className="p-5 rounded-2xl border border-border/70 bg-card hover:border-primary/40 transition-all cursor-pointer flex flex-col"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-sm text-foreground line-clamp-1 flex-1">{p.name}</h3>
                  <span className="text-[11px] font-mono bg-muted px-2 py-0.5 rounded-full shrink-0">
                    {p.total - p.done} tâche{p.total - p.done !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="text-[11px] border border-border rounded-full px-2 py-0.5 text-muted-foreground">Actif</span>
                  <span className={`text-[11px] border border-border rounded-full px-2 py-0.5 flex items-center gap-1 ${p.priorityClass}`}>
                    <Flag className="w-2.5 h-2.5" />{p.priority}
                  </span>
                  {p.deadline && (
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />{p.deadline}
                    </span>
                  )}
                </div>

                {p.total > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-[11px] text-muted-foreground">{p.done} / {p.total} tâches terminées</span>
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={inView ? { opacity: 1 } : {}}
                        transition={{ delay: 0.4 + i * 0.08 }}
                        className="text-[11px] font-semibold text-primary"
                      >
                        {pct}%
                      </motion.span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-primary rounded-full"
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${pct}%` } : {}}
                        transition={{ duration: 0.7, ease: "easeOut", delay: 0.25 + i * 0.08 }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-auto pt-3 border-t border-border/50">
                  {p.nextAction ? (
                    <div>
                      <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-1">Prochaine action</p>
                      <p className="text-xs font-medium flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-primary/60 shrink-0" />
                        <span className="line-clamp-1">{p.nextAction}</span>
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-orange-500 font-medium flex items-center gap-1.5">
                      <ArrowRight className="w-3.5 h-3.5" /> En attente d'action
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {demoProjects.map((p, i) => {
            const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -12 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.3, delay: 0.08 + i * 0.07 }}
                className="flex items-center gap-4 px-4 py-3 rounded-xl border border-border/70 bg-card hover:border-primary/40 transition-all cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm font-semibold truncate">{p.name}</span>
                    <span className="text-[11px] border border-border rounded-full px-2 py-0.5 text-muted-foreground shrink-0">Actif</span>
                    <span className={`text-[11px] border border-border rounded-full px-2 py-0.5 shrink-0 flex items-center gap-1 ${p.priorityClass}`}>
                      <Flag className="w-2.5 h-2.5" />{p.priority}
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-primary rounded-full"
                      initial={{ width: 0 }}
                      animate={inView ? { width: `${pct}%` } : {}}
                      transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 + i * 0.07 }}
                    />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-primary tabular-nums">{pct}%</p>
                  <p className="text-[11px] text-muted-foreground">{p.done}/{p.total}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── CHAT IA page ─────────────────────────────────────────────────────────────
const chatMessages = [
  { role: "user", text: "Qu'est-ce que je devrais faire en priorité aujourd'hui ?" },
  {
    role: "ai",
    text: "Voici votre priorité du jour :\n\n• 🔴 Répondre à Marc Girard — brief packaging Q3 bloqué, critique\n• 🟡 Refonte site Artemia : deadline 22 mai, seulement 33% avancé\n• 📬 8 mails en attente dont 2 avec délai cette semaine\n\nCommencez par Marc — il est bloqué sans vous.",
  },
  { role: "user", text: "Ajoute une tâche : appeler Sophie Lehmann demain 10h." },
  { role: "ai", text: "✓ Tâche créée : « Appeler Sophie Lehmann »\nDemain 10:00 · Priorité haute · Projet : Prospection Q2." },
];

function ChatView() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    chatMessages.forEach((_, i) => {
      setTimeout(() => setVisibleCount(i + 1), 300 + i * 700);
    });
  }, [inView]);

  return (
    <div ref={ref} className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border flex items-center gap-2 shrink-0">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold">Chat IA</p>
          <p className="text-[11px] text-muted-foreground">Votre assistant avec contexte complet</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <AnimatePresence>
          {chatMessages.slice(0, visibleCount).map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start gap-2.5"}`}
            >
              {msg.role === "ai" && (
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-3.5 h-3.5 text-primary" />
                </div>
              )}
              <div
                className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-tr-sm"
                    : "bg-muted/70 text-foreground rounded-tl-sm"
                }`}
              >
                {msg.text.replace(/\*\*(.*?)\*\*/g, "$1")}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Typing indicator */}
        {visibleCount > 0 && visibleCount < chatMessages.length && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2.5"
          >
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="flex items-center gap-1 bg-muted/70 px-3 py-2 rounded-2xl rounded-tl-sm">
              {[0, 0.15, 0.3].map((d, j) => (
                <motion.div
                  key={j}
                  className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50"
                  animate={{ y: [0, -4, 0] }}
                  transition={{ repeat: Infinity, duration: 0.6, delay: d }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Input bar */}
      <div className="px-6 py-4 border-t border-border shrink-0">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-4 py-2.5">
          <input
            readOnly
            placeholder="Posez votre question ou décrivez une tâche…"
            className="flex-1 text-sm bg-transparent outline-none text-muted-foreground placeholder:text-muted-foreground/60"
          />
          <button className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Send className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tour animated previews ───────────────────────────────────────────────────

function TourTodayPreview() {
  const items = [
    { label: "Répondre à Marc — devis Q3", done: true, tag: "IA: priorité max" },
    { label: "Préparer la revue produit",  done: false },
    { label: "Relire la proposition Acme", done: false },
  ];
  return (
    <div className="flex flex-col gap-1.5 p-3 h-full justify-center">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -14 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.14, duration: 0.28 }}
          className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 ${
            item.done ? "bg-primary/10 border border-primary/20" : "bg-muted/60 border border-border/50"
          }`}
        >
          {item.done ? (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring", stiffness: 400 }}>
              <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
            </motion.div>
          ) : (
            <Circle className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
          )}
          <span className={`text-[11px] flex-1 font-medium truncate ${item.done ? "line-through text-muted-foreground" : ""}`}>
            {item.label}
          </span>
          {item.tag && (
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 380 }}
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary shrink-0"
            >
              {item.tag}
            </motion.span>
          )}
        </motion.div>
      ))}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
        className="mt-1"
      >
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: "33%" }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.6 }}
          />
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground mt-0.5 px-0.5">
          <span>Progression</span><span className="font-semibold text-primary">1 / 3</span>
        </div>
      </motion.div>
    </div>
  );
}

function TourActionsPreview() {
  const cards = [
    { init: "M", sender: "Marc Dupont",    badge: "À répondre", color: "bg-blue-50 text-blue-700"   },
    { init: "S", sender: "Sophie Martin",  badge: "Planifier",  color: "bg-amber-50 text-amber-700" },
    { init: "A", sender: "Acme Corp",      badge: "Déléguer",   color: "bg-violet-50 text-violet-700"},
  ];
  return (
    <div className="flex flex-col gap-1.5 p-3 h-full justify-center">
      {cards.map((c, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: 18 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.13, duration: 0.3 }}
          className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-card border border-border/60"
        >
          <div className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">
            {c.init}
          </div>
          <span className="text-[11px] font-medium flex-1 truncate">{c.sender}</span>
          <motion.span
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: i * 0.13 + 0.22, type: "spring", stiffness: 360 }}
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${c.color}`}
          >
            {c.badge}
          </motion.span>
        </motion.div>
      ))}
    </div>
  );
}

function TourProjectsPreview() {
  const items = [
    { name: "Refonte site web Artemia Group", done: 2, total: 6 },
    { name: "Campagne lancement été 2026",    done: 1, total: 5 },
    { name: "Podcast « Les Créatifs »",       done: 1, total: 4 },
  ];
  return (
    <div className="flex flex-col gap-2.5 p-3 h-full justify-center">
      {items.map((p, i) => {
        const pct = Math.round((p.done / p.total) * 100);
        return (
          <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.12 }}>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-[10px] font-medium truncate flex-1">{p.name}</span>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.12 + 0.4 }}
                className="text-[10px] font-bold text-primary ml-2 shrink-0"
              >
                {pct}%
              </motion.span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.7, ease: "easeOut", delay: i * 0.12 + 0.15 }}
              />
            </div>
            <p className="text-[9px] text-muted-foreground mt-0.5">{p.done} / {p.total} tâches</p>
          </motion.div>
        );
      })}
    </div>
  );
}

function TourChatPreview() {
  const [phase, setPhase] = useState<"idle" | "typing" | "reply">("idle");
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("typing"), 400);
    const t2 = setTimeout(() => setPhase("reply"), 1400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="flex flex-col gap-2 p-3 h-full justify-center">
      <motion.div
        initial={{ opacity: 0, x: 14 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.26 }}
        className="flex justify-end"
      >
        <div className="bg-primary text-primary-foreground text-[10px] px-3 py-1.5 rounded-2xl rounded-tr-sm max-w-[80%]">
          Résume ma semaine et ce qui est critique
        </div>
      </motion.div>

      <AnimatePresence>
        {phase === "typing" && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-1.5 px-1">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-2.5 h-2.5 text-primary" />
            </div>
            <div className="flex items-center gap-1 bg-muted/70 px-2.5 py-1.5 rounded-2xl rounded-tl-sm">
              {[0, 0.15, 0.3].map((d, j) => (
                <motion.div key={j} className="w-1 h-1 rounded-full bg-muted-foreground/50"
                  animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 0.55, delay: d }} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {phase === "reply" && (
          <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.26 }} className="flex items-start gap-1.5">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="w-2.5 h-2.5 text-primary" />
            </div>
            <div className="bg-muted/80 text-[10px] px-2.5 py-1.5 rounded-2xl rounded-tl-sm max-w-[85%] leading-relaxed">
              <span className="font-semibold text-primary">6 actions</span> en attente dont 2 critiques. Commencez par Marc — son devis expire vendredi.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Tour widget ──────────────────────────────────────────────────────────────
const TOUR_STEPS = [
  {
    title: "Aujourd'hui",
    desc: "L'IA sélectionne vos 3 à 5 priorités du jour parmi toutes vos tâches. Fini les listes sans fin — vous savez exactement par où commencer.",
    page: "today",
    Icon: Sun,
    color: "text-amber-500",
    bg: "bg-amber-50",
    accent: "bg-amber-400",
    Preview: TourTodayPreview,
  },
  {
    title: "À faire",
    desc: "Chaque mail analysé par l'IA devient une action concrète. Répondre, planifier, déléguer — traitez votre boîte en quelques clics.",
    page: "actions",
    Icon: CheckSquare,
    color: "text-blue-500",
    bg: "bg-blue-50",
    accent: "bg-blue-400",
    Preview: TourActionsPreview,
  },
  {
    title: "Projets",
    desc: "Regroupez vos tâches en projets avec notes et fichiers. Visualisez l'avancement de chaque initiative d'un seul coup d'œil.",
    page: "projects",
    Icon: Folder,
    color: "text-violet-500",
    bg: "bg-violet-50",
    accent: "bg-violet-400",
    Preview: TourProjectsPreview,
  },
  {
    title: "Chat IA",
    desc: "Un assistant qui connaît tout votre contexte. Demandez-lui de créer des tâches, de résumer votre semaine ou de planifier votre sprint.",
    page: "chat",
    Icon: MessageSquare,
    color: "text-rose-500",
    bg: "bg-rose-50",
    accent: "bg-rose-400",
    Preview: TourChatPreview,
  },
];

const tourSlide = {
  enter: (d: number) => ({ opacity: 0, x: d * 28 }),
  center: { opacity: 1, x: 0 },
  exit:  (d: number) => ({ opacity: 0, x: -d * 28 }),
};

function TourWidget({ currentPage, onGo }: { currentPage: PageId; onGo: (p: PageId) => void }) {
  const [step, setStep] = useState(0);
  const [dir, setDir]   = useState(1);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const idx = TOUR_STEPS.findIndex((ts) => ts.page === currentPage);
    if (idx >= 0 && idx !== step) {
      setDir(idx > step ? 1 : -1);
      setStep(idx);
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = (i: number) => {
    setDir(i > step ? 1 : -1);
    setStep(i);
    onGo(TOUR_STEPS[i].page as PageId);
  };

  const s = TOUR_STEPS[step];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm"
    >
      <div className="rounded-2xl border border-border bg-background/97 backdrop-blur-md shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <Layers className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex-1">
            Visite guidée
          </span>
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {step + 1}<span className="opacity-40">/{TOUR_STEPS.length}</span>
          </span>
        </div>

        {/* Animated preview pane */}
        <div className="relative h-[130px] border-y border-border bg-muted/20 overflow-hidden">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              variants={tourSlide}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.26, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <s.Preview />
            </motion.div>
          </AnimatePresence>
          {/* Left colour accent */}
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${s.accent}`} />
        </div>

        {/* Text content */}
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            variants={tourSlide}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="px-4 pt-3 pb-3"
          >
            <div className="flex items-start gap-2.5">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${s.bg}`}>
                <s.Icon className={`w-3.5 h-3.5 ${s.color}`} />
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-snug">{s.title}</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Footer: dots + nav / CTA */}
        <div className="px-4 pb-3 pt-1 flex items-center justify-between border-t border-border/40">
          <div className="flex items-center gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-200 ${
                  i === step
                    ? "w-5 h-2 bg-primary"
                    : "w-2 h-2 bg-muted-foreground/25 hover:bg-muted-foreground/50"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm" variant="ghost"
              onClick={() => goTo(Math.max(0, step - 1))}
              disabled={step === 0}
              className="h-7 px-3 text-xs"
            >
              ← Précédent
            </Button>
            {step < TOUR_STEPS.length - 1 ? (
              <Button
                size="sm"
                onClick={() => goTo(step + 1)}
                className="h-7 px-3 text-xs gap-1"
              >
                Suivant <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <Link href="/sign-up">
                <Button size="sm" className="h-7 px-3 text-xs gap-1 bg-primary hover:bg-primary/90">
                  S'inscrire gratuitement <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* CTA banner on last step */}
        {step === TOUR_STEPS.length - 1 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.25 }}
            className="px-4 pb-3"
          >
            <div className="rounded-xl bg-primary/8 border border-primary/20 px-3 py-2.5 text-center">
              <p className="text-[11px] font-semibold text-foreground mb-0.5">Prêt à gagner du temps ?</p>
              <p className="text-[10px] text-muted-foreground">Créez votre compte et connectez votre boîte mail en 2 minutes.</p>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const PAGE_LABELS: Record<PageId, string> = {
  today: "Aujourd'hui", actions: "À faire", projects: "Projets", chat: "Chat IA",
};

const slideVariants = {
  enter: (d: number) => ({ opacity: 0, x: d * 40 }),
  center: { opacity: 1, x: 0 },
  exit: (d: number) => ({ opacity: 0, x: -d * 40 }),
};

export default function DemoPage() {
  const [page, setPage] = useState<PageId>("today");
  const [direction, setDirection] = useState(1);
  const [autoPlay, setAutoPlay] = useState(true);

  // Auto-cycle through pages
  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => {
      setDirection(1);
      setPage((p) => {
        const i = PAGES.indexOf(p);
        return PAGES[(i + 1) % PAGES.length];
      });
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [autoPlay]);

  const navigateTo = (p: PageId) => {
    setDirection(PAGES.indexOf(p) > PAGES.indexOf(page) ? 1 : -1);
    setPage(p);
    setAutoPlay(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <Sidebar active={page} onSelect={navigateTo} />

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Demo banner */}
        <DemoBanner />

        {/* Mobile page title */}
        <div className="md:hidden px-4 py-3 border-b border-border flex items-center gap-2">
          <span className="font-semibold text-sm">{PAGE_LABELS[page]}</span>
        </div>

        {/* Animated page content — pb-16 on mobile to clear bottom nav */}
        <div className="flex-1 min-h-0 relative overflow-hidden pb-16 md:pb-0">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={page}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="absolute inset-0 flex flex-col overflow-hidden"
            >
              {page === "today"    && <TodayView />}
              {page === "actions"  && <ActionsView />}
              {page === "projects" && <ProjectsView />}
              {page === "chat"     && <ChatView />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <MobileBottomNav active={page} onSelect={navigateTo} />

      {/* Floating tour widget */}
      <TourWidget currentPage={page} onGo={navigateTo} />
    </div>
  );
}
